import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import feedbackService from '../services/feedbackService';

/**
 * "You were at {event} — RATE IT" — the post-event rating prompt on the Events
 * page. Dynamic: fetches the most recent ended event the viewer attended but
 * hasn't rated yet, and RATE IT drops them onto that event's feedback form.
 * Renders nothing when there's nothing to rate.
 */

// A short "when" label from the event date: LAST NIGHT / TODAY / the weekday.
function whenLabel(iso) {
  if (!iso) return 'RECENTLY';
  const then = new Date(iso);
  const now = new Date();
  const dayMs = 24 * 60 * 60 * 1000;
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfThen = new Date(then.getFullYear(), then.getMonth(), then.getDate()).getTime();
  const daysAgo = Math.round((startOfToday - startOfThen) / dayMs);
  if (daysAgo <= 0) return 'EARLIER TODAY';
  if (daysAgo === 1) return 'LAST NIGHT';
  if (daysAgo < 7) return then.toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase();
  return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase();
}

export default function PostEventFeedbackBanner() {
  const navigate = useNavigate();
  const [pending, setPending] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await feedbackService.getPending();
      if (!cancelled && r.success && r.data) setPending(r.data);
    })();
    return () => { cancelled = true; };
  }, []);

  if (!pending || dismissed) return null;

  const rate = () => navigate(`/events/${pending.inviteCode || pending.eventId}?rate=1`);

  return (
    <div className="mx-auto mt-4 mb-2 max-w-[1600px] px-4 sm:px-6">
      {/* Stacks on mobile (so the event title never truncates) and sits on one
          row on larger screens. */}
      <div className="relative border-2 border-black bg-black px-4 py-4 text-white sm:flex sm:items-center sm:gap-4 sm:px-6">
        {/* Dismiss — top-right on mobile, inline on desktop. */}
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          className="absolute right-2 top-2 font-mono text-lg leading-none text-white/40 hover:text-white sm:static sm:order-3"
        >
          ×
        </button>
        <div className="min-w-0 flex-1 pr-6 sm:pr-0">
          <div className="font-mono text-[10px] font-bold uppercase tracking-widest text-white/50">
            {whenLabel(pending.dateTime)}
          </div>
          <div className="mt-0.5 font-mono text-sm leading-snug text-white">
            You were at <b>{pending.title}</b>.
            <span className="hidden sm:inline"> One question makes next weekend better.</span>
          </div>
        </div>
        <button
          type="button"
          onClick={rate}
          className="mt-3 w-full flex-shrink-0 bg-glitch-green px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-widest text-black hover:opacity-90 sm:mt-0 sm:w-auto sm:order-2"
        >
          Rate it
        </button>
      </div>
    </div>
  );
}
